import React, { useEffect, useMemo, useRef, useState } from "react";
import PageTitle from "../../components/PageTitle/PageTitle";
import Datagrid from "../../components/Datagrid/Datagrid";
import { useSite } from "../../context/siteContext";
import { Alert, Box, Link } from "@mui/material";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import { useCart } from "../../context/cartContext";
import { ASSETS_BASE_URL, RSPL_LIST } from "../../config";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import DeleteIcon from "@mui/icons-material/Delete";
import { ALERT_MESSAGES, LABELS, TABLE_HEADERS } from "../../constants";
import { useTranslation } from "react-i18next";
import { formatCurrency } from "../../utilities/currency";
import { getSavedForLaterBySiteId, removeFromSavedForLater } from "../../services/savedForLetterService";
import DataGridSkeleton from "../../components/PageSkeletons/DataGridSkeleton";
import UserName from "../../components/UserName/UserName";
import useBrandingAssets from "../../hooks/useBrandingAssets";
import { useOptions } from "../../context/optionContext";
import { buildRsplCodeToLabelMap, getRsplLabelForCode, parseRsplList } from "../../utilities/rspl";
import ReplacementPartNotice from "../../components/ReplacementPartNotice/ReplacementPartNotice";
import { useAuth } from "../../context/authContext";
import { trackSearchPerformed } from "../../utilities/analyticsService";



const Requests = () => {
	const [savedForlater, setSavedForlater] = useState([]);
	const [loading, setloading] = useState(true);
	const [selectedSFLRows, setSelectedSFLRows] = useState([]);
	const [error, setError] = useState("");
	const { selectedSite, selectedSiteCurrency } = useSite();
	const navigate = useNavigate();
	const { addMultipleToCart } = useCart();
	const { canViewProductPrice, canAddToCart } = useAuth();
	const {t} = useTranslation();
	const { stockImageUrl } = useBrandingAssets();
	const { optionKeys } = useOptions();
	const lastTrackedSearchRef = useRef("");
	const resolvedRsplList = useMemo(
		() => parseRsplList(optionKeys?.rsplList, RSPL_LIST),
		[optionKeys?.rsplList]
	);
	const rsplMapping = useMemo(
		() => buildRsplCodeToLabelMap(resolvedRsplList),
		[resolvedRsplList]
	);
	const canShowPrice = canViewProductPrice();
	const canShowMoveToCart = canAddToCart();
	const getPartTypeLabel = (row = {}) =>
		getRsplLabelForCode(row?.product_rspl_code, rsplMapping, t('UI_TEXT.OTHER'));
	const resolveProductRsplCode = (item = {}) => (
		item?.product_rspl_code
		|| item?.productData?.product_rspl_code
		|| item?.productData?.rspl_code
		|| item?.rspl_code
		|| ""
	);

	useEffect(() => {
		const fetchReqList = async () => {
			try {
				const response = await getSavedForLaterBySiteId(selectedSite);
				if (response.status) {
					const products = response.data;
					const purchasableProducts = products
						.filter((product) => product.purchasable === 1)
						.map((product) => {
							const key = `price_${selectedSiteCurrency}`;
							const currencyPrice = product[key];
							const finalPrice = product.purchasable ? currencyPrice : 0;
							return {
								product_id: product.product_id,
								product_name: product.product_name,
								product_number: product.product_number,
								asset_data: product.asset_data,
								product_rspl_code: resolveProductRsplCode(product),
								machine_id: product.machine_id,
								machine_number: product.machine_number,
								product_price: finalPrice,
								added_by: product.user_display_name || `${product?.user_first_name || ""} ${product?.user_last_name || ""}`.trim(),
								user_display_name: product.user_display_name || "",
								user_first_name: product.user_first_name || "",
								user_last_name: product.user_last_name || "",
								alternative_item_number: product.alternative_item_number,
								replacement: product.replacement,
							};
						});
					setSavedForlater(purchasableProducts);
					setloading(false);
				}
			} catch (error) {
				console.error("Error fetching Saved for Later Products:", error);
				setError(error);
			}
		};
		if (selectedSite !== "") fetchReqList();
	}, [selectedSite]);

	const handleRowSelect = (rows) => {
		setSelectedSFLRows(rows);
	};

	const handleAddtoCart = async () => {
		if (!canShowMoveToCart) return;
		// Check if any rows are selected
		if (selectedSFLRows.length === 0) {
			// Show alert if no users are selected
			Swal.fire({
				icon: "warning",
				title: t('ALERT_MESSAGES.NO_PARTS_SELECTED'),
				text: t('ALERT_MESSAGES.PLEASE_SELECE_PRODUCTS_ADD_TO_CART'),
			});
			return;
		}
		const purchasableProducts = [];
		selectedSFLRows.forEach((productId) => {
			const product = savedForlater.find((product) => product.product_id === productId);
			if (product) {
				const productEntry = {
					ProductID: product.product_id,
					Quantity: 1,
					Site: selectedSite,
				};
				purchasableProducts.push(productEntry);
			}
		});
		if (purchasableProducts.length > 0) {
			await addMultipleToCart(purchasableProducts);
			Swal.fire({
				title: t('ALERT_MESSAGES.SUCCESS'),
				text: t('ALERT_MESSAGES.PARTS_ADD_TO_CART'),
				icon: "success",
			});
			// Remove the added products from the saved for later list
			const remainingProducts = savedForlater.filter((product) => !selectedSFLRows.includes(product.product_id));
			setSavedForlater(remainingProducts);

			// Remove the added products from the saved_for_later table
			try {
				await removeFromSavedForLater(selectedSFLRows, selectedSite);
			} catch (error) {
				console.error("Error removing part(s) from saved for later:", error);
			}
		}
		// Confirm deletion if users are selected
	};

	const handleRemoveFromSavedForLater = async () => {
		// Check if any rows are selected
		if (selectedSFLRows.length === 0) {
			// Show alert if no products are selected
			Swal.fire({
				icon: "warning",
				title: t('ALERT_MESSAGES.NO_PARTS_SELECTED'),
				text: t('ALERT_MESSAGES.SELECT_PARTS_REMOVE'),
			});
			return;
		}

		try {
			// Remove the selected products from the saved_for_later table
			await removeFromSavedForLater(selectedSFLRows, selectedSite);

			// Remove the selected products from the saved for later list
			const remainingProducts = savedForlater.filter((product) => !selectedSFLRows.includes(product.product_id));
			setSavedForlater(remainingProducts);

			Swal.fire({
				title: t('ALERT_MESSAGES.SUCCESS'),
				text: t('ALERT_MESSAGES.PARTS_REMOVED_SAVED_LATED'),
				icon: "success",
			});
		} catch (error) {
			console.error("Error removing part(s) from saved for later:", error);
			Swal.fire({
				icon: "error",
				title: t('ALERT_MESSAGES.ERROR'),
				text: t('ALERT_MESSAGES.FAILED_TO_REMOVE_PART_SAVED'),
			});
		}
	};
	


	const customButtons = [
		...(canShowMoveToCart ? [{
			label:t('LABELS.MOVE_TO_CART'),
			onClick: () => {
				handleAddtoCart();
			},
			icon: <ShoppingCartIcon />,
		}] : []),
		{
			label: t('LABELS.REMOVE_SELECTED'),
			onClick: () => {
				handleRemoveFromSavedForLater();
			},
			icon: <DeleteIcon />,
		},
	];

	const handleSearch = ({ query = "", data = [] } = {}) => {
		const term = String(query || "").trim().toLowerCase();
		const rows = Array.isArray(data) ? data : [];
		const filteredRows = term
			? rows.filter((row) => Object.values(row || {}).some((value) => String(value ?? "").toLowerCase().includes(term)))
			: rows;
		const trackingKey = `${term}|${selectedSite}|${filteredRows.length}`;
		if (term && lastTrackedSearchRef.current !== trackingKey) {
			lastTrackedSearchRef.current = trackingKey;
			trackSearchPerformed({
				searchTerm: term,
				searchSource: "saved_for_later",
				pageName: "Saved For Later",
				resultCount: filteredRows.length,
			});
		}
		return filteredRows;
	};

	const columns = [
		{
			field: "product_name",
			headerName: t('TABLE_HEADERS.PART_NAME'),
			flex: 2,
			renderCell: (params) => {
				const defaultImage = stockImageUrl || `${ASSETS_BASE_URL}/stock.png`;

				const handleError = (e) => {
					e.target.onerror = null; // Prevent infinite loop
					e.target.src = defaultImage; // Fallback to default image
				};

				const src = (() => {
					const raw = params.row?.asset_data?.images?.[0];
					if (raw) {
						if (/^(https?:)?\/\//.test(raw) || raw.startsWith("data:")) return raw;
						const path = raw.startsWith("/") ? raw : `/${raw}`;
						return `${ASSETS_BASE_URL}${path}`;
					}
					return `${ASSETS_BASE_URL}/ProductImages/TMB/${params.row.product_number}.JPG`;
				})();

				return (
					<div style={{ display: "flex", alignItems: "center", gap: "8px", justifyContent: "flex-start" }}>
						<img
							src={src}
							alt={params.row.product_name}
							onError={handleError}
							style={{
								width: "40px",
								height: "40px",
								objectFit: "cover",
								borderRadius: "4px",
							}}
						/>
						<div>
							<Link
								sx={{ cursor: "pointer" }}
								variant="body2"
								onClick={() => {
									navigate(`/machines/${params.row.machine_id}/${params.row.product_id}`);
								}}>
								{params.row.product_name}
							</Link>
						</div>
					</div>
				);
			},
		},
		{
			field: "product_number",
			headerName: t('TITLES.PART_NUMBER'),
			flex: 1,
			renderCell: (params) => (
				<Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center", height: "100%" }}>
					<span>{params.value}</span>
					<ReplacementPartNotice product={params.row} />
				</Box>
			),
		},
		{
			field: "part_type",
			headerName: t('UI_TEXT.PART_TYPE', { defaultValue: 'Part Type' }),
			flex: 1,
			renderCell: (params) => getPartTypeLabel(params?.row),
		},
		{
			field: "machine_number", headerName: t('TABLE_HEADERS.MACHINE'), flex: 1, headerAlign: 'center',
			align: 'center',
		},
		...(canShowPrice ? [{
			field: "product_price",
			headerName: t('TABLE_HEADERS.PRICE'),
			headerAlign: 'center',
			align: 'center',
			flex: 1,
			renderCell: (params) => {
				return (
					<div>
						{formatCurrency(params.row.product_price, selectedSiteCurrency)}
						{/* {currencySymbol}
						{params.row.product_price} */}
					</div>
				);
			},
		}] : []),
		{
			field: "added_by",
			headerName: t('TABLE_HEADERS.ADDED_BY'),
			flex: 1,
			renderCell: (params) =>
				params?.value === "Deleted User" ? (
					<span style={{ color: "#aaa", fontSize: "11px", fontStyle: "italic" }}> N/A </span>
				) : params?.value === "" ? (
					<span style={{ color: "#aaa", fontSize: "11px", fontStyle: "italic" }}> N/A </span>
				) : (
					<Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "flex-start", height: "100%", width: "100%" }}>
						<UserName name={params.row.user_display_name || `${params.row.user_first_name || ''} ${params.row.user_last_name || ''}`.trim()} />
					</Box>
				),
		},
	];

	if (loading) {
		return (
			<DataGridSkeleton
				containerClassName="order-history-container"
				actionButtons={2}
				columns={4}
				rows={6}
			/>
		);
	}

	if (error) {
		return (
			<Alert severity="error" className="error-alert">
				{error}
			</Alert>
		);
	}

	return (
		<div className="order-history-container" data-tour="sfl-main">
			<PageTitle title={t('LABELS.SAVED_FOR_LATERS')} />

			{savedForlater.length === 0 && !loading ? (
				<Alert severity="info" className="error-alert" sx={{ marginTop: "10px" }}>
					{t('ALERT_MESSAGES.NO_PRODUCTS_IN_SAVED_LATER')}
				</Alert>
			) : (
					<Datagrid
						key="product_id"
						data={savedForlater}
						columns={columns}
					rowClick={false}
					pageSize={5}
					showSearchBar={true}
					checkboxSelection={true}
					customButtons={customButtons}
						handleSearch={handleSearch}
						onSelect={handleRowSelect}
						rowIdField="product_id"
						sortBy={[{ field: "product_name", sort: "asc" }]}
						hideFilterFromColumnMenu
						lockActiveColumnInManageColumns
						preventHidingLastColumn
					/>
			)}
		</div>
	);
};

export default Requests;
